import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LuLoaderCircle, LuSquareMinus } from "react-icons/lu";
import { useState } from "react";
import { removeAccountBrand } from "@/screens/analysis/apiService";
import { useParams } from "react-router-dom";

interface RemoveCompetitorDialogProps {
  brandName: string;
  brandId: string;
  onRemove: () => void;
  onRollback?: () => void;
}

export function RemoveCompetitorDialog({
  brandName,
  brandId,
  onRemove,
  onRollback,
}: RemoveCompetitorDialogProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { accountId } = useParams();

  const handleRemove = async () => {
    try {
      setIsLoading(true);

      // Optimistic update: remove immediately from UI
      onRemove();
      setShowConfirm(false);

      // Call API
      await removeAccountBrand({
        accountId: accountId!,
        brandId,
      });
    } catch (error) {
      console.error(error);
      // Rollback: restore the brand in UI if API fails
      if (onRollback) {
        onRollback();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
      <Button
        variant="outline"
        onClick={() => setShowConfirm(true)}
        className="hidden group-hover:flex h-6 w-6 p-0"
      >
        <LuSquareMinus className="shrink-0 text-[#D92D20]" size={14} />
      </Button>
      <DialogContent hideCloseButton className="p-[24px] max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-[#344054] font-[Inter] text-[16px] font-semibold leading-[28px]">
            Are you absolutely sure?
          </DialogTitle>
          <DialogDescription className="text-[#71717A] font-[Inter] text-[13px] font-normal leading-[20px]">
            Confirm removal of{" "}
            <span className="text-[#344054] font-medium">'{brandName}'</span> brand and
            its data. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setShowConfirm(false)}
            disabled={isLoading}
          >
            <span className="text-brand-primary-new text-[12px] font-medium leading-[19.157px] tracking-[0.24px]">
              Cancel
            </span>
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleRemove()}
            disabled={isLoading}
            className="min-w-[80px] bg-[#D92D20]"
          >
            <span className=" text-white text-[12px] font-[500] leading-[19.157px] tracking-[0.24px]">
              {isLoading ? (
                <LuLoaderCircle className="animate-spin text-white shrink-0" size={14} />
              ) : (
                "Remove"
              )}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
